import React, { useState } from 'react';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Eye,
  CheckCircle2,
  Send,
  Download,
  Calendar,
  User,
  Layers,
  ArrowRight,
  Shield
} from 'lucide-react';
import { PracticeReport, ReportStatus, PracticeClient, PracticeCase } from '../../types/practiceManagement';
import { Language, CompleteKundali } from '../../types/kundali';
import { UserProfile } from '../../types/user';
import { ReportBuilder } from './ReportBuilder';
import { ReportPreview } from './ReportPreview';
import { ConsultingReportingCentre } from './ConsultingReportingCentre';
import { AstrologerKundaliCountView } from './AstrologerKundaliCountView';
import { Users } from 'lucide-react';

interface ReportingCentreProps {
  reports: PracticeReport[];
  clients: PracticeClient[];
  cases: PracticeCase[];
  kundali?: CompleteKundali;
  lang: Language;
  currentUser?: UserProfile | null;
  onSaveReport: (report: PracticeReport) => void;
  onApproveReport: (reportId: string) => void;
  onDeliverReport: (reportId: string) => void;
}

export const ReportingCentre: React.FC<ReportingCentreProps> = ({
  reports,
  clients,
  cases,
  kundali,
  lang,
  currentUser,
  onSaveReport,
  onApproveReport,
  onDeliverReport
}) => {
  const isHi = lang === 'hi';
  const [activeReportTab, setActiveReportTab] = useState<'practice' | 'consulting' | 'astrologer_volume'>('astrologer_volume');
  const [isBuildingReport, setIsBuildingReport] = useState<boolean>(false);
  const [selectedReportForPreview, setSelectedReportForPreview] = useState<PracticeReport | null>(null);
  const [editingReport, setEditingReport] = useState<PracticeReport | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filteredReports = reports.filter((r) => {
    const matchSearch =
      r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.reportNumber.toLowerCase().includes(searchTerm.toLowerCase());

    const matchStatus = statusFilter === 'ALL' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      {/* Top Main Reporting Centre Tabs */}
      <div className="bg-astro-bg p-2 rounded-2xl border border-astro-border shadow-xs flex flex-wrap items-center gap-2">
        <button
          onClick={() => setActiveReportTab('astrologer_volume')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer ${
            activeReportTab === 'astrologer_volume'
              ? 'bg-[#C82A1E] text-white shadow-xs'
              : 'bg-white border border-astro-border text-astro-secondary-text hover:bg-astro-card'
          }`}
        >
          <Users className="w-4 h-4 text-amber-300" />
          <span>{isHi ? 'ज्योतिषी कुण्डली निर्माण विवरणी' : 'Astrologer Kundali Volume Report'}</span>
        </button>

        <button
          onClick={() => setActiveReportTab('consulting')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer ${
            activeReportTab === 'consulting'
              ? 'bg-astro-primary text-white shadow-xs'
              : 'bg-white border border-astro-border text-astro-secondary-text hover:bg-astro-card'
          }`}
        >
          <FileText className="w-4 h-4 text-astro-accent" />
          <span>{isHi ? 'परामर्श ज्योतिषी बेसिक रिपोर्ट्स' : 'Consulting Astrologer Basic Reports'}</span>
        </button>

        <button
          onClick={() => setActiveReportTab('practice')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer ${
            activeReportTab === 'practice'
              ? 'bg-astro-primary text-white shadow-xs'
              : 'bg-white border border-astro-border text-astro-secondary-text hover:bg-astro-card'
          }`}
        >
          <Layers className="w-4 h-4 text-astro-accent" />
          <span>{isHi ? 'अभ्यास डोजियर रिपोर्ट्स (Practice Dossiers)' : 'Practice Dossier Reports'}</span>
        </button>
      </div>

      {activeReportTab === 'astrologer_volume' ? (
        <AstrologerKundaliCountView lang={lang} currentUser={currentUser} />
      ) : activeReportTab === 'consulting' ? (
        <ConsultingReportingCentre currentUser={currentUser} lang={lang} kundali={kundali} />
      ) : (
        <>
          {/* Practice Dossiers View */}
          {isBuildingReport || editingReport ? (
            <ReportBuilder
              clients={clients}
              cases={cases}
              initialReport={editingReport}
              kundali={kundali}
              lang={lang}
              onSaveReport={(rep) => {
                onSaveReport(rep);
                setIsBuildingReport(false);
                setEditingReport(null);
              }}
              onCancel={() => {
                setIsBuildingReport(false);
                setEditingReport(null);
              }}
            />
          ) : selectedReportForPreview ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setSelectedReportForPreview(null)}
                  className="px-3 py-1.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary text-xs font-bold text-astro-secondary-text dark:text-astro-muted cursor-pointer"
                >
                  ← Back to Report Repository
                </button>
              </div>
              <ReportPreview
                report={selectedReportForPreview}
                kundali={kundali}
                lang={lang}
                onApproveReport={(id) => {
                  onApproveReport(id);
                  setSelectedReportForPreview((prev) => (prev ? { ...prev, status: 'APPROVED' } : null));
                }}
                onDeliverReport={(id) => {
                  onDeliverReport(id);
                  setSelectedReportForPreview((prev) => (prev ? { ...prev, status: 'DELIVERED' } : null));
                }}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-astro-muted absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={isHi ? 'रिपोर्ट, क्लाइंट या संख्या से खोजें...' : 'Search reports by title, client or ID...'}
                      className="w-full pl-9 pr-3 py-2 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="ALL">{isHi ? 'सभी रिपोर्ट' : 'All Reports'}</option>
                    <option value="DRAFT">Draft</option>
                    <option value="READY_FOR_REVIEW">Ready for Review</option>
                    <option value="APPROVED">Approved</option>
                    <option value="DELIVERED">Delivered</option>
                  </select>
                </div>

                <button
                  onClick={() => setIsBuildingReport(true)}
                  className="min-h-[38px] px-3.5 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>{isHi ? '+ नई रिपोर्ट तैयार करें' : '+ Create Practice Dossier'}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {filteredReports.map((report) => (
                  <div
                    key={report.id}
                    className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs hover:shadow-md transition-all flex flex-col justify-between gap-3"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-[11px] font-mono font-bold text-[#52635F] dark:text-amber-400 bg-astro-secondary-surface dark:bg-amber-950/40 px-2 py-0.2 rounded border border-[#E2DED5] dark:border-amber-800">
                          {report.reportNumber}
                        </span>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            report.status === 'DELIVERED'
                              ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
                              : report.status === 'APPROVED'
                              ? 'bg-blue-500/15 text-blue-800 dark:text-blue-300'
                              : 'bg-astro-secondary-surface0/15 text-astro-secondary dark:text-amber-300'
                          }`}
                        >
                          {report.status}
                        </span>
                      </div>

                      <h3 className="font-bold text-astro-text dark:text-astro-muted text-sm line-clamp-1">
                        {report.title}
                      </h3>
                      <p className="text-xs font-semibold text-astro-secondary-text dark:text-astro-muted pt-0.5">
                        Client: {report.clientName} ({report.clientRefId})
                      </p>
                      <p className="text-[11px] text-astro-muted line-clamp-2 italic pt-1">
                        "{report.targetQuestion}"
                      </p>
                    </div>

                    <div className="space-y-2 pt-2 border-t border-astro-border dark:border-astro-strong-border">
                      <div className="flex justify-between text-[11px] text-astro-muted">
                        <span>By: {report.generatedBy}</span>
                        <span>{report.generatedDate}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedReportForPreview(report)}
                          className="flex-1 min-h-[34px] px-2.5 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs flex items-center justify-center gap-1 transition-all cursor-pointer shadow-xs"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>{isHi ? 'पूर्वावलोकन' : 'Preview Document'}</span>
                        </button>

                        <button
                          onClick={() => setEditingReport(report)}
                          className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-card text-astro-secondary-text dark:text-astro-muted transition-colors cursor-pointer border border-astro-border dark:border-astro-strong-border"
                          title="Edit Modules"
                        >
                          <Layers className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredReports.length === 0 && (
                <div className="p-12 text-center bg-white dark:bg-slate-850 rounded-xl border border-astro-border/80 dark:border-astro-strong-border/80 text-xs text-astro-muted">
                  No practice reports found matching your criteria.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
